import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AdminUserRow, AdminPaged } from '@repo/shared-types';
import { adminFetchApi } from '../lib/api';
import type { AdminListParams } from './useAdminOrgs';

export function useAdminUsers(p: AdminListParams) {
  const qs = new URLSearchParams({ page: String(p.page), limit: String(p.limit), sort: p.sort });
  if (p.q) qs.set('q', p.q);
  return useQuery({
    queryKey: ['admin', 'users', p],
    queryFn: () => adminFetchApi<AdminPaged<AdminUserRow>>(`/admin/users?${qs}`),
  });
}

export function useSendResetLink() {
  return useMutation({
    mutationFn: (userId: string) => adminFetchApi(`/admin/users/${userId}/send-reset`, { method: 'POST' }),
    onSuccess: () => toast.success('Đã gửi link đặt lại mật khẩu'),
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useVerifyUserEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminFetchApi(`/admin/users/${userId}/verify-email`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('Đã đánh dấu email là đã xác thực');
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useStartImpersonation() {
  return useMutation({
    mutationFn: (userId: string) =>
      adminFetchApi<{ code: string; url: string }>(`/admin/users/${userId}/impersonate`, { method: 'POST' }),
    onSuccess: (data) => {
      // A new tab, deliberately: the impersonated session lives in that tab's
      // sessionStorage, so this admin tab keeps whatever session it has.
      //
      // Deliberately NOT passing 'noopener' (or 'noreferrer', which implies
      // it): per the HTML spec, window.open() returns null whenever the
      // noopener browsing-context flag is set, REGARDLESS of whether the tab
      // actually opened. Measured directly in a real browser with a real
      // click gesture: with 'noopener' the return value was null on a
      // successful open, every time — so `if (!popup)` was a 100% false
      // positive, firing on every impersonation and pointing the admin at a
      // "Mở tab" retry that reopens an already-spent (60s single-use) code.
      // Without it, `popup` is a real Window on success and null when a
      // browser genuinely blocks it, which is the only way to tell the two
      // apart. The opened page is our own dashboard (a trusted first-party
      // origin, not third-party content), so the window.opener reference
      // this leaves behind is not a reverse-tabnabbing exposure here. Do not
      // re-add 'noopener' — it silently breaks this detection again.
      const popup = window.open(data.url, '_blank');
      if (!popup) {
        // Some browsers still block a window.open that isn't a direct
        // synchronous consequence of the click (e.g. one dispatched from a
        // .then() callback). The grant code is only good for 60 seconds, so
        // a toast with a bare URL to copy would already be useless — the
        // action button's own onClick runs inside a real click gesture, so
        // it is never blocked the same way.
        toast.error('Trình duyệt đã chặn cửa sổ mới.', {
          action: {
            label: 'Mở tab',
            onClick: () => window.open(data.url, '_blank'),
          },
        });
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useChangeUserEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, email }: { userId: string; email: string }) =>
      adminFetchApi(`/admin/users/${userId}/email`, { method: 'PUT', body: JSON.stringify({ email }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('Đã đổi email');
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
